/**
 * WHICH RECORDS A DELIBERATE RE-SEND OR A REMINDER MAY GO TO, AND WHAT EACH ACT IS CALLED.
 *
 * The server decides; this exists so the screen says the same thing about the same record, for the
 * same reason the first send's copy does. It is a copy of `backend/src/domain/email/bulk-send.ts`
 * and the two are held together by `tests/fixtures/bulk-send-eligibility.json`: each package runs
 * its own implementation against every vector in that file, so a rule changed on one side fails on
 * the other until it is learned there too. The reason sentences come from the same file.
 *
 * The three acts are NAMED here as well, in one place, because the window, the register's bar and
 * the history all have to call them the same thing. An operator who presses «Hatırlat» and reads
 * «İlk e-postayı gönder» in the result has been told two different things about one act.
 */
import type { FirstSendDecision } from './first-send';
import { FIRST_SEND_REASON_TEXT } from './first-send';

export const BULK_SEND_KINDS = ['resend', 'reminder'] as const;
export type BulkSendKind = (typeof BULK_SEND_KINDS)[number];

export const SEND_REQUEST_KINDS = ['first_send', 'resend', 'reminder'] as const;
export type SendRequestKind = (typeof SEND_REQUEST_KINDS)[number];

export const BULK_SEND_DECISIONS = [
  'eligible',
  'no_previous_email',
  'nothing_to_remind',
  'delivery_in_flight',
  'no_recipient_email',
  'recipient_email_invalid',
  'awaiting_admin_publish',
  'declined',
  'revoked',
  'superseded',
  'expired',
] as const;
export type BulkSendDecision = (typeof BULK_SEND_DECISIONS)[number];

/**
 * WHAT ONLY THE WORKER CAN DECIDE.
 *
 * `BULK_SEND_DECISIONS` are facts about the RECORD, which is why the preview and the worker can read
 * the same rule and the operator can be told the truth before pressing. These are facts about the
 * WORK: they cannot exist at preview time, because they are about what happened between the operator
 * accepting a send and the worker reaching that target. They are never returned by
 * `decideBulkSend` — they are written by the worker as a target's reason — and they live here only
 * so there is ONE place where a reason code and its sentence are agreed on.
 *
 *   `newer_delivery_exists`  the record has been e-mailed since this request was accepted. Two
 *                            requests frozen while nothing had been sent cannot see each other, and
 *                            the one worked second is answering for a world that has moved; posting
 *                            anyway would also rotate the token and break the link the first message
 *                            just delivered. A send started AFTER an operation finished is not this
 *                            case: the comparison is «since this request», not «ever».
 *
 *   `claim_token_changed`    the record's acceptance link has changed since this request was
 *                            accepted, WITHOUT anybody being e-mailed — revoked directly, revoked
 *                            with the record's own lifecycle, or re-printed to be sent from outside
 *                            the product. The code above cannot see those, because none of them
 *                            leaves a delivery behind. Only a message that would mint a token can
 *                            be refused for this: a notice carries the public page and takes
 *                            nobody's link away, so it is not its business.
 */
export const WORK_TIME_DECISIONS = [
  'newer_delivery_exists',
  'claim_token_changed',
  'claim_link_handed_out',
] as const;
export type WorkTimeDecision = (typeof WORK_TIME_DECISIONS)[number];

export interface BulkSendFacts {
  viewState: string;
  recipientEmail: string | null;
  deliveries: readonly string[];
}

export interface BulkSendVerdict {
  decision: BulkSendDecision;
  message?: 'claim' | 'notice';
  /** True when sending this will mint a new acceptance link and stop the current one working. */
  rotatesToken?: boolean;
}

/**
 * A delivery that is FINISHED, in the sense that nothing more will be posted for it on its own.
 *
 * The settled list rather than the in-flight one, so a status this build has never seen blocks a
 * second message instead of being waved through. `soft_bounced` is deliberately absent: the outbox
 * still has a retry coming for it. `sent` is present: the provider has taken the message, and
 * waiting for a delivery confirmation many providers never send would block every re-send forever.
 */
const SETTLED: ReadonlySet<string> = new Set(['sent', 'delivered', 'hard_bounced', 'failed', 'suppressed']);

const normalize = (email: string): string =>
  String(email ?? '')
    .trim()
    .toLowerCase();
const plausible = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 320;

export function decideBulkSend(kind: BulkSendKind, facts: BulkSendFacts): BulkSendVerdict {
  switch (facts.viewState) {
    case 'pending_admin_publish':
      return { decision: 'awaiting_admin_publish' };
    case 'declined':
      return { decision: 'declined' };
    case 'revoked':
      return { decision: 'revoked' };
    case 'superseded':
      return { decision: 'superseded' };
    case 'expired':
      return { decision: 'expired' };
    default:
      break;
  }

  /* A reminder is about an OUTSTANDING action, and only one state has one. */
  if (kind === 'reminder' && facts.viewState !== 'pending_acceptance')
    return { decision: 'nothing_to_remind' };

  let any = false;
  for (const status of facts.deliveries) {
    const s = status.trim();
    if (s.length === 0) continue;
    if (!SETTLED.has(s)) return { decision: 'delivery_in_flight' };
    any = true;
  }
  if (!any) return { decision: 'no_previous_email' };

  const to = normalize(facts.recipientEmail ?? '');
  if (!to) return { decision: 'no_recipient_email' };
  if (!plausible(to)) return { decision: 'recipient_email_invalid' };

  const claim = facts.viewState === 'pending_acceptance';
  return { decision: 'eligible', message: claim ? 'claim' : 'notice', rotatesToken: claim };
}

export function isBulkSendEligible(v: BulkSendVerdict): boolean {
  return v.decision === 'eligible';
}

/** One sentence per reason, Turkish, and the same sentences the shared contract file holds. */
export const BULK_SEND_REASON_TEXT: Record<BulkSendDecision, string> = {
  eligible: 'Gönderilebilir.',
  no_previous_email: 'Bu kayda hiç e-posta gönderilmemiş; yeniden gönderim değil, ilk gönderim kullanılmalı.',
  nothing_to_remind: 'Bekleyen bir aksiyon yok; hatırlatma gönderilmez.',
  delivery_in_flight: 'Bu kayıt için bir gönderim hâlâ yolda; sonucu belirlenene kadar yenisi oluşturulmaz.',
  no_recipient_email: 'Alıcı e-posta adresi yok.',
  recipient_email_invalid: 'Alıcı e-posta adresi geçerli değil.',
  awaiting_admin_publish: 'Kayıt yönetici yayınını bekliyor; yayından önce e-posta gönderilmez.',
  declined: 'Alıcı reddetti.',
  revoked: 'Kayıt iptal edildi.',
  superseded: 'Kayıt yenisiyle değiştirildi.',
  expired: 'Kaydın süresi doldu.',
};

/**
 * The reason sentence for a code from EITHER rule.
 *
 * The two vocabularies are disjoint by construction — `already_has_email` can only come from a
 * first send and `nothing_to_remind` only from a reminder — so one lookup can serve both without
 * ever having to be told which act asked. `eligible` is the single shared code and both tables say
 * something true about it.
 */
/**
 * The sentences for the reasons only the worker can write.
 *
 * Separate table, same file as its codes, and read by `sendReasonText` like any other — so a target
 * the worker skipped for a work-time reason reads as a sentence on the screen rather than as a bare
 * code, which is what «ineligible records are never shown as silent success» needs.
 */
export const WORK_TIME_REASON_TEXT: Record<WorkTimeDecision, string> = {
  newer_delivery_exists:
    'Bu talep kabul edildikten sonra aynı kayda başka bir e-posta gönderilmiş; ikinci bir mesaj oluşturulmadı.',
  claim_token_changed:
    'Bu talep kabul edildikten sonra kaydın kabul bağlantısı değişmiş; çalışan bağlantıyı iptal edecek ikinci bir mesaj oluşturulmadı.',
  claim_link_handed_out:
    'Kaydın kabul bağlantısı bu talep kabul edildikten sonra bir dosyayla paylaşıldı ve ham değeri okunamadı; birinin elindeki bağlantıyı iptal edecek yeni bir mesaj oluşturulmadı.',
};

/**
 * The sentences for a target that FAILED, which is a different thing from one that was passed over.
 *
 * These codes are not verdicts any preview can reach: they are what the worker writes when working
 * one target threw. The panel printed them raw — «Başarısız — lock_not_available» — so an operator
 * reading the per-person results of their own send was handed an internal identifier.
 */
export const SEND_FAILURE_REASON_TEXT: Record<string, string> = {
  send_failed: 'Bu kayıt işlenirken teknik bir hata oluştu; mesaj gönderilmedi.',
  lock_not_available:
    'Kayıt o anda başka bir işlem tarafından kilitliydi; mesaj gönderilmedi, yeniden denenebilir.',
  serialization_conflict:
    'Kayıt aynı anda değiştiği için işlem geri alındı; mesaj gönderilmedi, yeniden denenebilir.',
  persistence_error: 'Veritabanına yazılamadı; mesaj gönderilmedi, yeniden denenebilir.',
};

export function sendReasonText(code: string): string {
  return (
    FIRST_SEND_REASON_TEXT[code as FirstSendDecision] ??
    BULK_SEND_REASON_TEXT[code as BulkSendDecision] ??
    WORK_TIME_REASON_TEXT[code as WorkTimeDecision] ??
    code
  );
}

/**
 * What a FAILED target says on screen.
 *
 * A known code becomes its sentence. An unknown one — the worker can surface any error code the
 * send path throws, and that set is not closed — becomes a generic sentence with the code kept in
 * brackets: hiding it would cost the operator the only detail they could relay, and printing it
 * alone is what this function exists to stop.
 */
export function sendFailureText(code: string | null): string {
  const raw = (code ?? '').trim();
  if (!raw) return 'Bu kayıt işlenirken teknik bir hata oluştu; mesaj gönderilmedi.';
  const known = SEND_FAILURE_REASON_TEXT[raw];
  if (known) return known;
  return `Bu kayıt işlenirken teknik bir hata oluştu; mesaj gönderilmedi. (${raw})`;
}

/** What one act is called, everywhere it is named. */
export interface SendAct {
  /** The act's own name: «Hatırlatma». Used in the history and in the bar. */
  name: string;
  /** The window's heading before anything has been accepted. */
  askTitle: string;
  /** The window's heading once a request is being watched. */
  resultTitle: string;
  /** The button that accepts it, without its count. */
  action: string;
  /** What the operator starts out saying they are doing; they may rewrite it. */
  defaultReason: string;
  /** The one line under the heading: which records this act is for. */
  rule: string;
  /** What to say when the scope contains nothing this act can go to. */
  none: string;
}

export const SEND_ACTS: Record<SendRequestKind, SendAct> = {
  first_send: {
    name: 'İlk e-posta',
    askTitle: 'İlk e-postayı gönder',
    resultTitle: 'İlk e-posta gönderim sonucu',
    action: 'İlk e-postayı gönder',
    defaultReason: 'İlk e-posta gönderimi',
    rule: 'Yalnız henüz hiç e-posta gönderilmemiş kayıtlara gönderilir.',
    none: 'Bu kapsamda ilk gönderime uygun kayıt yok.',
  },
  resend: {
    name: 'Yeniden gönderim',
    askTitle: 'Aynı e-postayı yeniden gönder',
    resultTitle: 'Yeniden gönderim sonucu',
    action: 'Yeniden gönder',
    defaultReason: 'Alıcının isteği üzerine yeniden gönderim',
    rule: 'Yalnız daha önce e-posta gönderilmiş ve sonucu belirlenmiş kayıtlara gönderilir.',
    none: 'Bu kapsamda yeniden gönderilebilecek kayıt yok.',
  },
  reminder: {
    name: 'Hatırlatma',
    askTitle: 'Kabul bekleyenlere hatırlatma gönder',
    resultTitle: 'Hatırlatma sonucu',
    action: 'Hatırlatma gönder',
    defaultReason: 'Kabul bekleyen alıcılara hatırlatma',
    rule: 'Yalnız hâlâ kabul bekleyen kayıtlara gönderilir; kabul etmiş kişiye hatırlatma gitmez.',
    none: 'Bu kapsamda hatırlatma gönderilecek kayıt yok.',
  },
};

export function sendAct(kind: string): SendAct {
  return SEND_ACTS[kind as SendRequestKind] ?? SEND_ACTS.first_send;
}

/**
 * The sentence that states what a claim-carrying send costs, before it is accepted.
 *
 * The raw token is never stored, only its hash, so a link that has already been sent cannot be
 * rebuilt: a new acceptance e-mail mints a new one and the old link stops working. Saying that
 * afterwards is not the same as saying it first.
 */
export function rotationWarning(count: number): string | null {
  if (count <= 0) return null;
  return (
    `${count} kayıt için yeni bir onay bağlantısı üretilecek; o kayıtların ` +
    'daha önce gönderilmiş bağlantıları çalışmayı bırakır.'
  );
}
